import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Shield, 
  ChevronRight,
  AlertTriangle,
  ArrowLeft,
  Crown,
  User,
  ChevronDown,
  LogOut
} from 'lucide-react';

const API = '';

const SuperAdminTechnologies = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Version state
  const [availableVersions, setAvailableVersions] = useState([]); // [{version, category_count, technology_count, is_active}]
  const [activeVersion, setActiveVersion] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null); // number | 'all'
  const [settingActive, setSettingActive] = useState(false);
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showTechnologyDialog, setShowTechnologyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTechnology, setEditingTechnology] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    emoji: '📦',
    description: '',
    order: 0
  });
  
  const [technologyForm, setTechnologyForm] = useState({
    name: '',
    category_id: ''
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  useEffect(() => {
    if (selectedVersion !== null) {
      fetchCategories();
      fetchAllTechnologies();
    }
  }, [selectedVersion]);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/tech-versions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const { active_version, versions } = response.data;
      setActiveVersion(active_version);
      setAvailableVersions(versions);
      // Default view to active version
      setSelectedVersion(active_version);
    } catch (error) {
      toast.error('Failed to load catalog versions');
      console.error(error);
      setSelectedVersion(1);
    }
  };

  const fetchCategories = async () => {
    try {
      const versionParam = selectedVersion === 'all' ? 'all' : selectedVersion;
      const response = await axios.get(
        `${API}/api/admin/technology-categories?version=${versionParam}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setCategories(response.data);
      if (response.data.length > 0 && !selectedCategory) {
        setSelectedCategory(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTechnologies = async () => {
    try {
      const versionParam = selectedVersion === 'all' ? 'all' : selectedVersion;
      const response = await axios.get(
        `${API}/api/admin/technologies?version=${versionParam}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setTechnologies(response.data);
    } catch (error) {
      toast.error('Failed to load technologies');
      console.error(error);
    }
  };

  const handleSetActiveVersion = async (version) => {
    if (version === 'all' || version === activeVersion) return;
    setSettingActive(true);
    try {
      await axios.put(
        `${API}/api/admin/tech-versions/active`,
        { version },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setActiveVersion(version);
      toast.success(`Version ${version} is now active. New audits will use it.`);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set active version');
    } finally {
      setSettingActive(false);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      emoji: '📦',
      description: '',
      order: categories.length + 1
    });
    setShowCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      emoji: category.emoji,
      description: category.description || '',
      order: category.order
    });
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/api/admin/technology-categories/${editingCategory.id}`,
          categoryForm,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Category updated successfully');
      } else {
        // When creating while viewing a specific version, pin to that version; fallback to active
        const targetVersion = (selectedVersion !== 'all' && typeof selectedVersion === 'number')
          ? selectedVersion
          : activeVersion;
        await axios.post(
          `${API}/api/admin/technology-categories`,
          { ...categoryForm, version: targetVersion },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Category created successfully');
      }
      setShowCategoryDialog(false);
      fetchCategories();
      fetchVersions();
    } catch (error) {
      toast.error('Failed to save category');
      console.error(error);
    }
  };

  const handleCreateTechnology = (categoryId = null) => {
    setEditingTechnology(null);
    setTechnologyForm({
      name: '',
      category_id: categoryId || selectedCategory?.id || ''
    });
    setShowTechnologyDialog(true);
  };

  const handleEditTechnology = (technology) => {
    setEditingTechnology(technology);
    setTechnologyForm({
      name: technology.name,
      category_id: technology.category_id
    });
    setShowTechnologyDialog(true);
  };

  const handleSaveTechnology = async () => {
    try {
      if (editingTechnology) {
        await axios.put(
          `${API}/api/admin/technologies/${editingTechnology.id}`,
          technologyForm,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Technology updated successfully');
      } else {
        const targetVersion = (selectedVersion !== 'all' && typeof selectedVersion === 'number')
          ? selectedVersion
          : activeVersion;
        await axios.post(
          `${API}/api/admin/technologies`,
          { ...technologyForm, version: targetVersion },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Technology created successfully');
      }
      setShowTechnologyDialog(false);
      fetchAllTechnologies();
      fetchVersions();
    } catch (error) {
      toast.error('Failed to save technology');
      console.error(error);
    }
  };

  const handleDeleteClick = (item, type) => {
    setDeletingItem({ ...item, type });
    setDeleteConfirmation('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== deletingItem.name) {
      toast.error('Confirmation text does not match');
      return;
    }

    setIsDeleting(true);
    try {
      if (deletingItem.type === 'category') {
        await axios.delete(
          `${API}/api/admin/technology-categories/${deletingItem.id}?confirmation=${encodeURIComponent(deleteConfirmation)}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Category and its technologies deleted');
        fetchCategories();
        setSelectedCategory(null);
      } else {
        await axios.delete(
          `${API}/api/admin/technologies/${deletingItem.id}?confirmation=${encodeURIComponent(deleteConfirmation)}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Technology deleted');
        fetchAllTechnologies();
      }
      setShowDeleteDialog(false);
      setDeletingItem(null);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
      </div>
    );
  }

  // Calculate all technologies count
  const allTechnologiesCount = categories.reduce((total, cat) => {
    const catTechs = technologies.filter(t => t.category_id === cat.id);
    return total + catTechs.length;
  }, 0);

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">All Technologies</h1>
                  <p className="text-sm text-slate-600">Manage master data for technologies</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Version Selector */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Catalog Version</p>
                  <p className="text-xs text-slate-500">New audits always use the Active version</p>
                </div>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="tech-version-tabs">
                  {availableVersions.map(v => (
                    <button
                      key={v.version}
                      onClick={() => setSelectedVersion(v.version)}
                      data-testid={`tech-version-tab-v${v.version}`}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        selectedVersion === v.version
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      v{v.version}
                      {v.is_active && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full" data-testid={`tech-version-active-badge-v${v.version}`}>
                          Active
                        </span>
                      )}
                      <span className="text-xs text-slate-400">({v.technology_count})</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedVersion('all')}
                    data-testid="tech-version-tab-all"
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      selectedVersion === 'all'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    All
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedVersion !== 'all' && selectedVersion !== activeVersion && (
                  <Button
                    size="sm"
                    onClick={() => handleSetActiveVersion(selectedVersion)}
                    disabled={settingActive}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="set-active-version-btn"
                  >
                    {settingActive ? 'Setting…' : `Set v${selectedVersion} as Active`}
                  </Button>
                )}
                {activeVersion !== null && (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="current-active-version-badge">
                    Active: v{activeVersion}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Categories</CardDescription>
              <CardTitle className="text-3xl">{categories.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Technologies</CardDescription>
              <CardTitle className="text-3xl">{allTechnologiesCount}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Search and Add Category */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search technologies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={handleCreateCategory} className="bg-navy-600 hover:bg-navy-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>

        {/* Categories with Technologies */}
        <div className="space-y-6">
          {categories.map((category) => {
            const categoryTechnologies = technologies.filter(t => 
              t.category_id === category.id &&
              (!searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase()))
            );

            return (
              <Card key={category.id}>
                <CardHeader className="bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{category.emoji}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">{category.name}</CardTitle>
                          <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-200" data-testid={`category-version-badge-${category.id}`}>
                            v{category.version ?? 1}
                          </Badge>
                        </div>
                        <CardDescription>
                          {categoryTechnologies.length} {categoryTechnologies.length === 1 ? 'technology' : 'technologies'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedCategory(category);
                          handleCreateTechnology(category.id);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Technology
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditCategory(category)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteClick(category, 'category')}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {categoryTechnologies.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryTechnologies.map((technology) => (
                        <div
                          key={technology.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-emerald-300 transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-navy-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-900">{technology.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditTechnology(technology)}
                              className="h-7 w-7 p-0"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteClick(technology, 'technology')}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 text-sm mb-3">
                        {searchTerm ? 'No technologies found matching your search' : 'No technologies in this category yet'}
                      </p>
                      {!searchTerm && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCategory(category);
                            handleCreateTechnology(category.id);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Technology
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {categories.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No categories yet. Create your first category to get started.</p>
                <Button onClick={handleCreateCategory}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Category
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details' : 'Add a new technology category'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Identity & Access Management"
              />
            </div>
            <div className="space-y-2">
              <Label>Emoji *</Label>
              <Input
                value={categoryForm.emoji}
                onChange={(e) => setCategoryForm({ ...categoryForm, emoji: e.target.value })}
                placeholder="e.g., 🧠"
                maxLength={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={!categoryForm.name}>
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Technology Dialog */}
      <Dialog open={showTechnologyDialog} onOpenChange={setShowTechnologyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTechnology ? 'Edit Technology' : 'Create Technology'}</DialogTitle>
            <DialogDescription>
              {editingTechnology ? 'Update technology details' : 'Add a new technology'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Technology Name *</Label>
              <Input
                value={technologyForm.name}
                onChange={(e) => setTechnologyForm({ ...technologyForm, name: e.target.value })}
                placeholder="e.g., Azure Active Directory"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTechnologyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTechnology} disabled={!technologyForm.name}>
              {editingTechnology ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delete {deletingItem?.type === 'category' ? 'Category' : 'Technology'}
            </DialogTitle>
            <DialogDescription>
              {deletingItem?.type === 'category' ? (
                <span className="text-red-600 font-medium">
                  Warning: This will also delete all technologies in this category!
                </span>
              ) : (
                'This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Please type <strong>{deletingItem?.name}</strong> to confirm deletion:
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type name to confirm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteConfirmation !== deletingItem?.name || isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminTechnologies;
